import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Button, Text, TextInput, Textarea } from '@tremor/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function HypothesisFormModal({ isOpen, onClose, onSubmit, editHypothesis = null, loading = false }) {
    const { t } = useTranslation()
    const isEditing = !!editHypothesis
    
    // For editing individual hypothesis, keep old structure. For creating, use new structure
    const getInitialFormData = () => {
        if (isEditing && editHypothesis) {
            // Editing individual hypothesis - keep old structure
            return {
                hypothesis_statement: editHypothesis?.hypothesis_text || '',
                variables_description: editHypothesis?.variables_description || '',
                reasoning: editHypothesis?.reasoning || '',
                hypothesis_type: editHypothesis?.hypothesis_type || 'general',
                scope: editHypothesis?.scope || 'academic',
                status: editHypothesis?.status || 'draft'
            }
        } else {
            // Creating new hypothesis set - use new structure
            return {
                scope: 'academic',
                status: 'draft',
                reasoning: '',
                general_hypothesis_text: '',
                general_variables_description: '',
                specific_hypothesis_text: '',
                specific_variables_description: '',
                null_hypothesis_text: '',
                null_variables_description: ''
            }
        }
    }
    
    const [formData, setFormData] = useState(getInitialFormData)
    const [error, setError] = useState(null)

    // Update form data when editHypothesis prop changes or modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData(getInitialFormData())
            setError(null)
        }
    }, [editHypothesis, isOpen, isEditing])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: sanitizeUserInput(value)
        }))
    }


    const validateForm = () => {
        if (!formData.hypothesis_statement.trim()) {
            setError(t('Research.ObjectivosHipoteses.Hypotheses.StatementRequired'))
            return false
        }

        setError(null)
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) return

        let submitData
        
        if (isEditing) {
            // Individual hypothesis editing - keep old structure
            submitData = {
                hypothesis_text: formData.hypothesis_statement,
                variables_description: formData.variables_description,
                reasoning: formData.reasoning,
                hypothesis_type: formData.hypothesis_type,
                scope: formData.scope,
                status: formData.status,
                type: 'manual'
            }
        } else {
            // New hypothesis set creation - use new structure
            submitData = {
                scope: formData.scope,
                status: formData.status,
                reasoning: formData.reasoning,
                hypotheses: [
                    {
                        type: 'general',
                        hypothesis_text: formData.general_hypothesis_text,
                        variables_description: formData.general_variables_description
                    },
                    {
                        type: 'specific',
                        hypothesis_text: formData.specific_hypothesis_text,
                        variables_description: formData.specific_variables_description
                    },
                    {
                        type: 'null',
                        hypothesis_text: formData.null_hypothesis_text,
                        variables_description: formData.null_variables_description
                    }
                ],
                type: 'manual'
            }
        }

        try {
            await onSubmit(submitData)
            handleClose()
        } catch (err) {
            setError(err.error || t('Research.ObjectivosHipoteses.Hypotheses.SubmitError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData(getInitialFormData())
            setError(null)
            onClose()
        }
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {isEditing 
                                            ? t('Research.ObjectivosHipoteses.Hypotheses.EditHypothesis')
                                            : t('Research.ObjectivosHipoteses.Hypotheses.CreateHypothesisSet')
                                        }
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        disabled={loading}
                                        className="rounded-md p-2 hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {isEditing ? (
                                        // Individual hypothesis editing form
                                        <>
                                            {/* Hypothesis Type */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Research.ObjectivosHipoteses.Hypotheses.TypeLabel')}
                                                </label>
                                                <select
                                                    value={formData.hypothesis_type}
                                                    onChange={(e) => handleInputChange('hypothesis_type', e.target.value)}
                                                    disabled={loading}
                                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                >
                                                    <option value="general">{t('Research.ObjectivosHipoteses.Hypotheses.Types.general')}</option>
                                                    <option value="specific">{t('Research.ObjectivosHipoteses.Hypotheses.Types.specific')}</option>
                                                    <option value="null">{t('Research.ObjectivosHipoteses.Hypotheses.Types.null')}</option>
                                                </select>
                                            </div>

                                            {/* Hypothesis Statement */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Research.ObjectivosHipoteses.Hypotheses.StatementLabel')} *
                                                </label>
                                                <Textarea
                                                    placeholder={t('Research.ObjectivosHipoteses.Hypotheses.StatementPlaceholder')}
                                                    value={formData.hypothesis_statement}
                                                    onChange={(e) => handleInputChange('hypothesis_statement', e.target.value)}
                                                    rows={3}
                                                    disabled={loading}
                                                    className="w-full"
                                                />
                                            </div>

                                            {/* Variables Description */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionLabel')}
                                                </label>
                                                <Textarea
                                                    placeholder={t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionPlaceholder')}
                                                    value={formData.variables_description}
                                                    onChange={(e) => handleInputChange('variables_description', e.target.value)}
                                                    rows={3}
                                                    disabled={loading}
                                                    className="w-full"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Scope */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('Research.ObjectivosHipoteses.Hypotheses.ScopeLabel')}
                                                    </label>
                                                    <select
                                                        value={formData.scope}
                                                        onChange={(e) => handleInputChange('scope', e.target.value)}
                                                        disabled={loading}
                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                    >
                                                        <option value="academic">{t('Research.ObjectivosHipoteses.Scopes.Academic')}</option>
                                                        <option value="business">{t('Research.ObjectivosHipoteses.Scopes.Business')}</option>
                                                        <option value="mixed">{t('Research.ObjectivosHipoteses.Scopes.Mixed')}</option>
                                                    </select>
                                                </div>

                                                {/* Status */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('Research.ObjectivosHipoteses.Hypotheses.StatusLabel')}
                                                    </label>
                                                    <select
                                                        value={formData.status}
                                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                                        disabled={loading}
                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                    >
                                                        <option value="draft">{t('Research.ObjectivosHipoteses.Hypotheses.Status.draft')}</option>
                                                        <option value="active">{t('Research.ObjectivosHipoteses.Hypotheses.Status.active')}</option>
                                                        <option value="archived">{t('Research.ObjectivosHipoteses.Hypotheses.Status.archived')}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Reasoning (Optional) */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Research.ObjectivosHipoteses.Hypotheses.ReasoningLabel')} ({t('Common.Optional')})
                                                </label>
                                                <Textarea
                                                    placeholder={t('Research.ObjectivosHipoteses.Hypotheses.ReasoningPlaceholder')}
                                                    value={formData.reasoning}
                                                    onChange={(e) => handleInputChange('reasoning', e.target.value)}
                                                    rows={3}
                                                    disabled={loading}
                                                    className="w-full"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        // Hypothesis set creation form
                                        <>
                                            {/* Editable Hypotheses */}
                                            <div className="space-y-6">
                                                {/* General Hypothesis (H1) */}
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <span className="text-sm font-medium text-iavisus-100">
                                                            H1 - General Hypothesis
                                                        </span>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.StatementLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.StatementPlaceholder')}
                                                                value={formData.general_hypothesis_text}
                                                                onChange={(e) => handleInputChange('general_hypothesis_text', e.target.value)}
                                                                rows={3}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionPlaceholder')}
                                                                value={formData.general_variables_description}
                                                                onChange={(e) => handleInputChange('general_variables_description', e.target.value)}
                                                                rows={2}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Specific Hypothesis (H2-Hn) */}
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <span className="text-sm font-medium text-iavisus-100">
                                                            H2-Hn - Specific Hypothesis
                                                        </span>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.StatementLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.StatementPlaceholder')}
                                                                value={formData.specific_hypothesis_text}
                                                                onChange={(e) => handleInputChange('specific_hypothesis_text', e.target.value)}
                                                                rows={3}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionPlaceholder')}
                                                                value={formData.specific_variables_description}
                                                                onChange={(e) => handleInputChange('specific_variables_description', e.target.value)}
                                                                rows={2}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Null Hypothesis (H0) */}
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <span className="text-sm font-medium text-iavisus-100">
                                                            H0 - Null Hypothesis
                                                        </span>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.StatementLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.StatementPlaceholder')}
                                                                value={formData.null_hypothesis_text}
                                                                onChange={(e) => handleInputChange('null_hypothesis_text', e.target.value)}
                                                                rows={3}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionLabel')}
                                                            </label>
                                                            <Textarea
                                                                placeholder={t('Research.ObjectivosHipoteses.Hypotheses.VariablesDescriptionPlaceholder')}
                                                                value={formData.null_variables_description}
                                                                onChange={(e) => handleInputChange('null_variables_description', e.target.value)}
                                                                rows={2}
                                                                disabled={loading}
                                                                className="w-full"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Scope */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('Research.ObjectivosHipoteses.Hypotheses.ScopeLabel')}
                                                    </label>
                                                    <select
                                                        value={formData.scope}
                                                        onChange={(e) => handleInputChange('scope', e.target.value)}
                                                        disabled={loading}
                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                    >
                                                        <option value="academic">{t('Research.ObjectivosHipoteses.Scopes.Academic')}</option>
                                                        <option value="business">{t('Research.ObjectivosHipoteses.Scopes.Business')}</option>
                                                        <option value="mixed">{t('Research.ObjectivosHipoteses.Scopes.Mixed')}</option>
                                                    </select>
                                                </div>

                                                {/* Status */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('Research.ObjectivosHipoteses.Hypotheses.StatusLabel')}
                                                    </label>
                                                    <select
                                                        value={formData.status}
                                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                                        disabled={loading}
                                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                                    >
                                                        <option value="draft">{t('Research.ObjectivosHipoteses.Hypotheses.Status.draft')}</option>
                                                        <option value="active">{t('Research.ObjectivosHipoteses.Hypotheses.Status.active')}</option>
                                                        <option value="archived">{t('Research.ObjectivosHipoteses.Hypotheses.Status.archived')}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Reasoning (Optional) */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t('Research.ObjectivosHipoteses.Hypotheses.ReasoningLabel')} ({t('Common.Optional')})
                                                </label>
                                                <Textarea
                                                    placeholder={t('Research.ObjectivosHipoteses.Hypotheses.ReasoningPlaceholder')}
                                                    value={formData.reasoning}
                                                    onChange={(e) => handleInputChange('reasoning', e.target.value)}
                                                    rows={3}
                                                    disabled={loading}
                                                    className="w-full"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Error Message */}
                                    {error && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <Text className="text-red-600 text-sm">{error}</Text>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleClose}
                                            disabled={loading}
                                        >
                                            {t('Common.Cancel')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            loading={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {isEditing ? t('Common.Save') : t('Common.Create')}
                                        </Button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default HypothesisFormModal